import { APIURL, apiGet, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isEmptyWidgetData, toggleWidgetTheme, normalizeWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

/** Row shape consumed by the Price Performance v2 UI. */
interface Pp2RowData {
  periodLabel: string;
  periodLow: number;
  periodLowPct: number;
  periodHigh: number;
  periodHighPct: number;
  periodLowDateLabel: string;
  periodHighDateLabel: string;
  /** 0–100: colored segment width from the left */
  barWidthPct: number;
  barPositive: boolean;
  performance: number;
  performancePct: number;
  sparkPositive: boolean;
  sparkValues: number[];
}

/** Raw item shape returned by the Price Performance API (matches v1). */
interface PerformanceApiItem {
  /** Server ticker id when present (e.g. MSFT_NASDAQ). */
  tvId?: string;
  period: string;
  prevClosePrice: number;
  performanceLastDate: string;
  periodOpen: number;
  periodOpenPercentage: number;
  periodLow: number;
  periodLowPercentage: number;
  barPositionPercentage: number;
  periodHigh: number;
  periodHighPercentage: number;
  periodLowDate: string;
  periodHighDate: string;
  performance: number;
  performancePercentage: number;
  startClose: number;
  endClose: number;
  performanceDate: string;
  chart: number[];
}

interface PastPricePerformanceConfig extends WidgetConfig {
  interval?: string;
}

const PP2_INTERVAL_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '1d', label: '1D' },
  { value: '5d', label: '5D' },
];

function formatPp2Date(input?: string): string {
  if (!input) return '';
  const d = new Date(input);
  if (isNaN(d.getTime())) return `On ${input}`;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `On ${mm}/${dd}/${yyyy}`;
}

function mapApiItemToRow(item: PerformanceApiItem): Pp2RowData {
  const barWidth = Math.min(100, Math.max(0, Number(item.barPositionPercentage) || 0));
  const perfPct = Number(item.performancePercentage) || 0;
  const highPct = Number(item.periodHighPercentage) || 0;
  return {
    periodLabel: item.period,
    periodLow: Number(item.periodLow) || 0,
    periodLowPct: Number(item.periodLowPercentage) || 0,
    periodHigh: Number(item.periodHigh) || 0,
    periodHighPct: highPct,
    periodLowDateLabel: formatPp2Date(item.periodLowDate),
    periodHighDateLabel: formatPp2Date(item.periodHighDate),
    barWidthPct: barWidth,
    barPositive: highPct >= 0,
    performance: Number(item.performance) || 0,
    performancePct: perfPct,
    sparkPositive: perfPct >= 0,
    sparkValues: Array.isArray(item.chart) ? item.chart : [],
  };
}

export class TraderversePricePerformance2 {
  private container: HTMLElement;
  private config: PastPricePerformanceConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private shadowRoot: ShadowRoot;
  private scrollResizeObserver: ResizeObserver | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config } as PastPricePerformanceConfig;
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();
  }

  private getDefaultConfig(): Partial<PastPricePerformanceConfig> {
    return {
      headerPinned: true,
      theme: 'default-dark',
      width: '100%',
      height: 'auto',
      autosize: true,
      interval: '5d',
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const element = document.getElementById(container) || document.querySelector(container);
      if (!element) {
        throw new Error(`Container not found: ${container}`);
      }
      return element as HTMLElement;
    }
    return container;
  }

  private async loadECharts(): Promise<void> {
    if (!(window as any).echarts) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/echarts@5.4.3/dist/echarts.min.js';
      document.head.appendChild(script);

      return new Promise((resolve) => {
        script.onload = () => resolve();
      });
    }
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (id: string) => this.loadPerformanceData(id),
      (type: any, data: any) => this.emit(type, data),
    );
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const symbolDropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    symbolDropdown?.addEventListener('change', (e) => {
      const symbol = (e.target as HTMLSelectElement).value;
      this.changeSymbol(symbol);
    });
    if (symbolDropdown && this.config.symbol) {
      symbolDropdown.value = this.config.symbol;
    }

    const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
    if (filtersToggle && filtersBox) {
      filtersToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        filtersBox.classList.toggle('hidden');
      });
    }

    const intervalSelect = this.shadowRoot.getElementById('pp2-interval') as HTMLSelectElement | null;
    if (intervalSelect) {
      intervalSelect.value = this.config.interval || '5d';
      intervalSelect.addEventListener('change', () => {
        this.config.interval = intervalSelect.value || '5d';
        this.loadPerformanceData(this.config.symbol || 'MSFT');
      });
    }
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  private async init(): Promise<void> {
    await this.loadECharts();
    this.createWidget();
    await this.attachStyles();
    this.setupEventListeners();
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private filtersIconSvg(): string {
    return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
  }

  /** Same symbol list as {@link TraderversePricePerformance} for native select fallback. */
  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' },
      { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' },
      { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' },
      { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' },
      { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' },
      { label: 'COST', value: 'COST' },
      { label: 'CSCO', value: 'CSCO' },
      { label: 'ADBE', value: 'ADBE' },
      { label: 'TMUS', value: 'TMUS' },
      { label: 'AMD', value: 'AMD' },
      { label: 'NFLX', value: 'NFLX' },
      { label: 'INTC', value: 'INTC' },
      { label: 'AMGN', value: 'AMGN' },
      { label: 'QCOM', value: 'QCOM' },
      { label: 'TXN', value: 'TXN' },
      { label: 'HON', value: 'HON' },
    ];
  }

  private createWidget(): void {
    const themeClass =
      this.config.theme === 'white' ? 'whiteMode' : this.config.theme === 'black' ? 'blackMode' : this.config.theme === 'default-lite' ? 'defaultLightMode' : 'defaultDarkMode';
    const useFillLayout = Boolean(this.config.height && this.config.height !== 'auto');
    const fillClass = useFillLayout ? ' pp2-widget--fill' : '';
    const widgetHtml = `
              <div style="width: ${typeof this.config.width === 'number' ? this.config.width + 'px' : this.config.width};${this.config.height === 'auto' ? '' : `height: ${typeof this.config.height === 'number' ? this.config.height + 'px' : this.config.height};`}" class="widgetBox pp2-widget${fillClass} ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${this.config.theme}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Price Performance</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              ${
                this.config.hideSymbol
                  ? '<div class="item dropStyle01"></div>'
                  : `<div class="item dropStyle01">
                <div class="trader-sm-card" id="selected-symbol">
                ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'stock', name: '' })}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
                </div>`
              }
              <div class="item">
                <button class="filters-toggle" type="button"> ${this.filtersIconSvg()} </button>
              </div>
            </div>
          </div>
          <div class="inBody">
            <div class="filtersBox sideBar hidden">
              <div class="item">
                <select id="pp2-interval" class="dropdown-btn">
                  ${PP2_INTERVAL_OPTIONS.map((opt) => `<option value="${opt.value}" ${opt.value === (this.config.interval || '5d') ? 'selected' : ''}>${opt.label}</option>`).join('')}
                </select>
              </div>
            </div>

            <div class="chartDv pp2-body" id="pastPricePerformanceContainer">
            </div>

          </div>
        </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    if (!this.config.hideSymbol) {
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder) {
        if (this.config.isSymbolSelectable) {
          const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
          if (dropdown) placeholder.replaceWith(dropdown);
        } else {
          const symbolOptions = this.fetchSymbols()
            .map((s) => `<option value="${s.value}">${s.label}</option>`)
            .join('');
          const sel = document.createElement('select');
          sel.className = 'symbol-dropdown';
          sel.innerHTML = symbolOptions;
          placeholder.replaceWith(sel);
        }
      }
    }
    this.loadPerformanceData(this.config.symbol || 'MSFT');
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async loadPerformanceData(symbol: string = this.config.symbol || 'MSFT'): Promise<void> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    try {
      const apiItems = await this.fetchPerformanceData(symbol, this.config.interval || '5d');
      const rows = apiItems.map((item) => mapApiItemToRow(item));
      this.renderRows(rows);
      if (widget) widget.classList.remove('loading');
    } catch (error) {
      console.error('Failed to load performance data:', error);
      this.renderError();
      if (widget) widget.classList.remove('loading');
    }
  }

  private async fetchPerformanceData(symbol: string, interval: string): Promise<PerformanceApiItem[]> {
    const response = await apiGet({
      method: 'POST',
      url: `${APIURL}/v3/ticker/price_performance_v2`,
      body: { symbol, interval },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseData = await response.json();
    const list: any[] = Array.isArray(responseData?.data) ? responseData.data : [];
    return list.map((item: any) => ({
      tvId: item.tvId,
      period: item.period,
      prevClosePrice: item.prevClosePrice,
      performanceLastDate: item.performanceLastDate,
      periodOpen: item.periodOpen,
      periodOpenPercentage: item.periodOpenPercentage,
      periodLow: item.periodLow,
      periodLowPercentage: item.periodLowPercentage,
      barPositionPercentage: item.barPositionPercentage,
      periodHigh: item.periodHigh,
      periodHighPercentage: item.periodHighPercentage,
      periodLowDate: item.periodLowDate,
      periodHighDate: item.periodHighDate,
      performance: item.performance,
      performancePercentage: item.performancePercentage,
      startClose: item.startClose,
      endClose: item.endClose,
      performanceDate: item.performanceDate,
      chart: item.chart,
    }));
  }

  private formatUsd(n: number): string {
    return `$${Math.round(n)}`;
  }

  private formatPct(n: number): string {
    return `${n.toFixed(2)}%`;
  }

  private renderRows(data: Pp2RowData[]): void {
    const container = this.shadowRoot.querySelector('#pastPricePerformanceContainer');
        if (data && data.length > 0) {
        container?.setAttribute('data-testing-id', 'past-price-performance-container');
    } else {
        container?.removeAttribute('data-testing-id');
    }

    if (!container) return;
    if (isEmptyWidgetData(data)) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const headRow = `
    <div class="pp2-row pp2-row--head" role="row">
      <div class="pp2-head-left" aria-hidden="true"></div>
      <div class="pp2-head-mid">
        <span>Period Low</span>
        <span>Period High</span>
      </div>
      <div class="pp2-head-perf">Performance</div>
    </div>`;

    const html =
      headRow +
      data
        .map((item, index) => {
          const barPctCls = item.barPositive ? 'is-up' : 'is-down';
          const perfDeltaCls = item.sparkPositive ? 'is-up' : 'is-down';
          const tri = item.sparkPositive ? '▲' : '▼';
          const chartId = `pp2-chart-${index}`;
          return `
    <div class="pp2-row" role="row">
      <div class="pp2-period">${item.periodLabel}</div>
      <div class="pp2-mid">
        <div class="pp2-mid-pricebar">
          <span class="pp2-pw-low">${this.formatUsd(item.periodLow)}</span>
          <div class="pp2-bar-wrap">
            <div class="pp2-bar" aria-hidden="true">
              <div class="pp2-bar-fill ${barPctCls}" style="width:${Math.min(100, Math.max(0, item.barWidthPct))}%"></div>
            </div>
          </div>
          <span class="pp2-pw-high">${this.formatUsd(item.periodHigh)}</span>
        </div>
        <div class="pp2-mid-pcts">
          <span class="pp2-pct ${barPctCls}">${this.formatPct(item.periodLowPct)}</span>
          <span class="pp2-pct ${barPctCls}">${this.formatPct(item.periodHighPct)}</span>
        </div>
        <div class="pp2-mid-dates">
          <span class="pp2-date">${item.periodLowDateLabel}</span>
          <span class="pp2-date">${item.periodHighDateLabel}</span>
        </div>
      </div>
      <div class="pp2-perf">
        <div class="pp2-perf-line">
          <span class="pp2-perf-num">${item.performance.toFixed(2)}</span>
          <span class="pp2-perf-delta ${perfDeltaCls}">${tri} ${this.formatPct(item.performancePct)}</span>
        </div>
        <div id="${chartId}" class="pp2-spark"></div>
      </div>
    </div>`;
        })
        .join('');

    container.innerHTML = `<div class="pp2">${html}</div>`;

    this.attachPp2ScrollResizeObserver(container as HTMLElement);

    const paintCharts = () => {
      if (!(window as any).echarts) return;
      data.forEach((item, index) => {
        this.renderSparkline(`pp2-chart-${index}`, item);
      });
    };
    requestAnimationFrame(() => {
      paintCharts();
      requestAnimationFrame(() => {
        const ec = (window as any).echarts;
        if (!ec) return;
        data.forEach((_, index) => {
          const el = this.shadowRoot.getElementById(`pp2-chart-${index}`);
          if (el) ec.getInstanceByDom(el)?.resize();
        });
      });
    });
  }

  private attachPp2ScrollResizeObserver(container: HTMLElement): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.scrollResizeObserver?.disconnect();
    let raf = 0;
    this.scrollResizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const ec = (window as any).echarts;
        if (!ec) return;
        this.shadowRoot.querySelectorAll('[id^="pp2-chart-"]').forEach((el) => {
          ec.getInstanceByDom(el as HTMLElement)?.resize();
        });
      });
    });
    this.scrollResizeObserver.observe(container);
  }

  private renderSparkline(chartId: string, item: Pp2RowData): void {
    const chartDom = this.shadowRoot.getElementById(chartId);
    if (!chartDom || !(window as any).echarts) return;

    const echarts = (window as any).echarts;
    const existing = echarts.getInstanceByDom(chartDom);
    if (existing) existing.dispose();

    const myChart = echarts.init(chartDom);
    const neg = !item.sparkPositive;
    myChart.setOption({
      grid: { left: 0, right: 0, top: 2, bottom: 1 },
      xAxis: {
        type: 'category',
        show: false,
        data: item.sparkValues.map(() => ''),
      },
      yAxis: { type: 'value', show: false },
      series: [
        {
          data: item.sparkValues,
          type: 'line',
          smooth: 0.35,
          symbol: 'none',
          lineStyle: {
            color: neg ? '#ff4d4d' : '#00e651',
            width: 1.75,
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: neg ? 'rgba(255,77,77,0.22)' : 'rgba(0,255,87,0.18)' },
                { offset: 1, color: 'rgba(11,10,22,0)' },
              ],
            },
          },
        },
      ],
    });
  }

  private renderError(): void {
    const container = this.shadowRoot.querySelector('#pastPricePerformanceContainer');
    if (!container) return;
    container.innerHTML = '<div class="pp2"><div class="error" style="padding:16px;color:var(--text-content);">Failed to load data</div></div>';
  }

  public on(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  public off(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

    this.loadPerformanceData();
  }

  public refresh(): void {
    this.loadPerformanceData();
  }

  public destroy(): void {
    this.scrollResizeObserver?.disconnect();
    this.scrollResizeObserver = null;
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const event: WidgetEvent = { type, data };
      handlers.forEach((handler) => handler(event));
    }
  }

  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }
}

declare global {
  interface Window {
    TraderversePricePerformance2: typeof TraderversePricePerformance2;
  }
}

if (typeof window !== 'undefined') {
  window.TraderversePricePerformance2 = TraderversePricePerformance2;
}
